import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { get as _get } from 'lodash';

import { ClaimDetailServiceRequestModel } from '@amfam/claim/data-access';
import { ClaimUtilService } from '@amfam/claim/shared/utility';
import { CopyService } from '@amfam/shared/utility/shared-services';

// Models
import {
  ClaimRentalDataModel,
  ServiceRequestPartnerDataModel
} from '../../models/claim-rental-data.model';

@Component({
  selector: 'ds-claim-status-rentalcar',
  templateUrl: './claim-status-rentalcar.component.html',
  styleUrls: ['../../containers/auto-claim-status/auto-claim-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ClaimStatusRentalcarComponent implements OnInit {
  @Input() claimRentalCarData: ClaimRentalDataModel;
  @Input() rentalCoverage: boolean;
  @Input() rentalDependencyUnmet: boolean;
  @Input() rentalEligible: boolean;
  @Input() isPartner = false;
  @Input() rentalPhoneNumber: string;

  public rentalServiceRequested: boolean;
  public serviceStarted: string;
  public serviceEnd: string;
  public showRemainingDays: boolean;
  public remainingDays: number;
  public serviceRequestPartnerData: ServiceRequestPartnerDataModel = {
    serviceRequestPartnerName: '',
    businessAddress: {
      addressLine1: '',
      city: '',
      state: '',
      zipCode: ''
    },
    phoneNumber: '',
    serviceRequestPartnerType: ''
  };
  public rentalNoSelectionMessage: string;

  constructor(
    private claimUtilService: ClaimUtilService,
    private copyService: CopyService
  ) {}

  ngOnInit() {
    if (!this.rentalCoverage) {
      this.rentalNoSelectionMessage = this.isPartner
        ? this.copyService.getCopy('claims.partnerNoRentalCoverage1') +
          this.rentalPhoneNumber +
          this.copyService.getCopy('claims.partnerNoRentalCoverage2')
        : this.copyService.getCopy('claims.noRentalCoverage');
    } else if (!this.rentalDependencyUnmet && !this.rentalEligible) {
      this.rentalNoSelectionMessage = this.isPartner
        ? this.copyService.getCopy('claims.partnerRentalCoverageNotEligible1') +
          this.rentalPhoneNumber +
          this.copyService.getCopy('claims.partnerRentalCoverageNotEligible2')
        : this.copyService.getCopy('claims.rentalCoverageNotEligible');
    }

    this.isRentalServiceRequested(_get(this.claimRentalCarData, 'serviceRequestData'));
    if (this.rentalServiceRequested) {
      this.isDaysRemainingInRental(
        this.claimRentalCarData.serviceRequestData.serviceStartedOn,
        this.claimRentalCarData.serviceRequestData.numberOfRentalDays
      );
      if (this.showRemainingDays) {
        this.remainingDays = this.calculateRemainingDays(
          this.claimRentalCarData.serviceRequestData.serviceStartedOn,
          this.claimRentalCarData.serviceRequestData.numberOfRentalDays
        );
      }
      this.serviceStarted = this.claimUtilService.formatDate(
        this.claimRentalCarData.serviceRequestData.serviceStartedOn
      );
      this.serviceEnd = this.claimUtilService.formatDate(
        this.calculateRentalServiceEndDate(
          this.claimRentalCarData.serviceRequestData.serviceStartedOn,
          this.claimRentalCarData.serviceRequestData.numberOfRentalDays
        )
      );
      this.showServicePartnerData(this.claimRentalCarData.serviceRequestData);
    }
  }

  private isRentalServiceRequested(serviceRequestData: ClaimDetailServiceRequestModel) {
    if (serviceRequestData) {
      this.rentalServiceRequested = true;
    } else {
      this.rentalServiceRequested = false;
    }
  }

  private isDaysRemainingInRental(startDate: string, numberOfRentalDays: number) {
    const endDate = this.calculateRentalServiceEndDate(startDate, numberOfRentalDays);
    if (this.claimUtilService.isDateTodayOrAfter(endDate)) {
      this.showRemainingDays = true;
    } else {
      this.showRemainingDays = false;
    }
  }

  private calculateRemainingDays(startDate: string, numberOfRentalDays: number) {
    startDate = this.claimUtilService.addDays(startDate, 1);
    const rentalUsedInDays = this.claimUtilService.daysRemainingFromToday(startDate);
    return numberOfRentalDays + rentalUsedInDays; // rental used will be in negative days
  }

  private calculateRentalServiceEndDate(startDate: string, numberOfRentalDays: number) {
    numberOfRentalDays = numberOfRentalDays - 1;
    const serviceEndDate = this.claimUtilService.addDays(startDate, numberOfRentalDays);
    return serviceEndDate;
  }

  private showServicePartnerData(data: ClaimDetailServiceRequestModel) {
    this.serviceRequestPartnerData.serviceRequestPartnerName = _get(
      data,
      'serviceRequestPartner',
      ''
    );
    if (_get(data, 'specialist.addresses')) {
      if (data.specialist.addresses.length > 0) {
        this.serviceRequestPartnerData.businessAddress.addressLine1 =
          data.specialist.addresses[0].addr1;
        this.serviceRequestPartnerData.businessAddress.city = data.specialist.addresses[0].city;
        this.serviceRequestPartnerData.businessAddress.state = data.specialist.addresses[0].state;
        this.serviceRequestPartnerData.businessAddress.zipCode = data.specialist.addresses[0].zip5;
      }
    }
    if (_get(data, 'specialist.phones')) {
      if (data.specialist.phones.length > 0) {
        this.serviceRequestPartnerData.phoneNumber =
          data.specialist.phones[0].areaCode + data.specialist.phones[0].number;
      }
    }
  }
}
